/**
 * iter-338z — AI Task Creator dialog for Edit BOQ.
 * iter-338z3 — Preset dropdown (Wardrobe / Kitchen / TV Unit),
 * larger modal, sub-category auto-create confirmation chip.
 *
 * Flow:
 *   1. User picks a preset (or leaves it on Auto-detect) and types a plain
 *      instruction ("Wardrobe 8x6, TV unit 6x4").
 *   2. Click "Generate Preview" → POST /ai-suggest-item returns editable rows.
 *      Any row whose sub-category doesn't exist yet shows a chip with the
 *      auto-sequenced code that WILL be created on commit (e.g. "B06").
 *   3. Every field is amendable inline (title, category, qty, rates, GST, desc).
 *   4. Click "Add to BOQ" → POST /ai-commit-items appends to worksheet.
 *      The backend silently creates any missing sub-categories, sequencing
 *      the code based on the last sub-category of the parent main category.
 *   5. Caller receives the newly-committed items and refreshes its state.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, Trash2, Plus, Wand2, FolderPlus, BookmarkPlus, X, Building2, Image as ImageIcon, Upload, MessageSquare, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import CategoryDropdown from '@/components/boq/CategoryDropdown';
import UnitSelect, { STANDARD_UNITS } from '@/components/boq/UnitSelect';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CreditBadge } from '@/components/CreditBadge';

// iter-386u-bd — Cheap keyword matcher for AI Auto-Layering. Splits the
// instruction into words and pulls any library material whose label/spec/key
// matches. Adds their rate to the preview and stores in pivot_inclusions.

// iter-386u-bo/bs — Precision Inclusions Sync.
// Rules:
//  - If the description has a "**Inclusions:**" (or "Inclusions:") header, we
//    REPLACE the content BETWEEN that header and the next section header
//    (Exclusions / Remarks / Assumptions) with the current inclusions list.
//  - If no header exists, we append a fresh "Inclusions:" block.
//  - AI-generated free text ABOVE the header stays untouched (scope of work).
function syncInclusionsInDescription(currentDesc = '', inclusions = []) {
  const bullets = (inclusions || []).map((it) => `• ${it.label}${it.spec ? ` — ${it.spec}` : ''}${it.unit ? ` (${it.unit})` : ''}`);
  const desc = String(currentDesc || '');
  // Find "Inclusions:" header (with or without **markdown**)
  const headerRe = /(\*\*\s*Inclusions\s*:\s*\*\*|Inclusions\s*:)/i;
  const nextSectionRe = /(\*\*\s*(?:Exclusions|Remarks|Assumptions|Notes|Scope)\s*:\s*\*\*|(?:Exclusions|Remarks|Assumptions|Notes|Scope)\s*:)/i;
  const m = desc.match(headerRe);
  if (!m) {
    // No header — append a fresh block
    if (bullets.length === 0) return desc.trim();
    const block = `**Inclusions:**\n${bullets.join('\n')}`;
    return desc.trim() ? `${desc.trim()}\n\n${block}` : block;
  }
  const headerStart = m.index;
  const headerEnd = headerStart + m[0].length;
  // Find next section header AFTER the inclusions header
  const after = desc.slice(headerEnd);
  const nextMatch = after.match(nextSectionRe);
  const bodyEnd = nextMatch ? headerEnd + nextMatch.index : desc.length;
  // Build the new block
  const newBody = bullets.length > 0 ? `\n${bullets.join('\n')}\n\n` : '\n';
  return desc.slice(0, headerEnd) + newBody + desc.slice(bodyEnd);
}

function autoLayerPreview(preview, instruction, library) {
  const flatMaterials = [];
  for (const items of Object.values(library || {})) for (const m of items) flatMaterials.push(m);
  if (!flatMaterials.length) return preview;
  const needle = (instruction || '').toLowerCase();
  const words = new Set(needle.split(/[^a-z0-9]+/i).filter((w) => w.length > 3));
  const picked = [];
  for (const m of flatMaterials) {
    const hay = `${m.label} ${m.spec || ''} ${m.key || ''}`.toLowerCase();
    for (const w of words) {
      if (hay.includes(w)) { picked.push(m); break; }
    }
    if (picked.length >= 3) break;
  }
  if (!picked.length) return preview;
  // iter-386u-bf — auto-layer respects unit-mismatch bucketing so preview
  // rates match what the user sees after Generate.
  const taskUnit = (preview.unit || '').toLowerCase();
  let addRate = 0, addClient = 0, addLumpV = 0, addLumpC = 0;
  const inclusions = picked.map((m) => {
    const r = Number(m.rate) || 0;
    const cr = Number(m.client_rate) || r;
    const matches = m.unit && taskUnit && m.unit.toLowerCase() === taskUnit;
    if (matches) { addRate += r; addClient += cr; }
    else { addLumpV += r; addLumpC += cr; }
    return {
      key: m.key, label: m.label, spec: m.spec || '', unit: m.unit || '',
      category: m.category || '', rate: r, client_rate: cr,
      bucket: matches ? 'rate' : 'lump_sum', auto: true,
    };
  });
  return {
    ...preview,
    vendor_rate: (Number(preview.vendor_rate) || 0) + addRate,
    client_rate: (Number(preview.client_rate) || 0) + addClient,
    lump_sum_vendor: (Number(preview.lump_sum_vendor) || 0) + addLumpV,
    lump_sum_client: (Number(preview.lump_sum_client) || 0) + addLumpC,
    pivot_inclusions: inclusions,
  };
}
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel, SelectSeparator,
} from '@/components/ui/select';
// iter-359g: shared 7-option taxonomy — same list AI Assistant uses.
import { PROJECT_TYPES } from '@/lib/project_types';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'wardrobe', label: 'Wardrobe (18mm ply, laminate, Ebco/Enox)' },
  { value: 'kitchen', label: 'Modular Kitchen (BWR, soft-close)' },
  { value: 'tv_unit', label: 'TV Unit (laminate + veneer panel)' },
  { value: 'none', label: 'No preset' },
];

export default function AITaskCreatorDialog({ open, onOpenChange, worksheetId, onItemsCommitted, onRefineApplied, refineTargetItem, initialMode = 'single', initialProjectSegment }) {
  const [instruction, setInstruction] = useState('');
  const [preset, setPreset] = useState('auto');
  const [previews, setPreviews] = useState([]);
  const [matchedPresets, setMatchedPresets] = useState([]);
  // iter-365 — pricing model + markups so the vendor/client rate hints stay in
  // sync with the project's pricing model. Populated after every AI preview.
  // Primary Rate is ALWAYS Client on open; only a manual Vendor click changes it.
  const [pricingContext, setPricingContext] = useState({
    pricing_model: 'client_to_vendor',
    admin_markup_percentage: 20,
    client_markup_percentage: 20,
  });
  useEffect(() => {
    if (open) setPricingContext((pc) => ({ ...pc, pricing_model: 'client_to_vendor' }));
  }, [open]);
  // Server responses refresh markups only — never the user's Primary Rate choice.
  const mergePricingContext = (ctx) => setPricingContext((pc) => ({ ...pc, ...ctx, pricing_model: pc.pricing_model }));
  const [generating, setGenerating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [customPresets, setCustomPresets] = useState([]);
  // iter-386u-bp — Preset Scope Picker (browse-and-apply table)
  const [presetPickerOpen, setPresetPickerOpen] = useState(false);
  const [presetPickerQuery, setPresetPickerQuery] = useState('');
  const [saveTargetIdx, setSaveTargetIdx] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  // iter-386u-bc — Pivot Task Configurator (materials library + right-pane inclusions per preview)
  const [materialsLibrary, setMaterialsLibrary] = useState({});
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  // iter-338z12 — Full Project BOQ generator
  const [mode, setMode] = useState(initialMode || 'single'); // 'single' | 'full' | 'image'
  // iter-366 — when the parent changes initialMode (e.g. AI Assistant tile
  // clicks Upload from File), sync the tab so the dialog opens on the
  // right screen.
  useEffect(() => { if (initialMode) setMode(initialMode); }, [initialMode, open]);
  const [fullProjectType, setFullProjectType] = useState('3BHK Residential');
  const [fullBudget, setFullBudget] = useState('');
  const [fullBedrooms, setFullBedrooms] = useState('3');
  const [fullBathrooms, setFullBathrooms] = useState('2');
  const [fullAreaSqft, setFullAreaSqft] = useState('');
  const [fullNotes, setFullNotes] = useState('');
  // iter-359i — segment-specific Full-Project BOQ context (mirrors AI Assistant).
  const [fullLocation, setFullLocation] = useState('');
  const [fullFloor, setFullFloor] = useState('0');
  const [fullLiftAccess, setFullLiftAccess] = useState(true);
  const [fullPowerBackup, setFullPowerBackup] = useState(null); // null | 'yes' | 'no'
  const [fullHomeAutomation, setFullHomeAutomation] = useState(null);
  const [fullModularKitchen, setFullModularKitchen] = useState(null);
  const [fullWorkstations, setFullWorkstations] = useState('20');
  const [fullCabins, setFullCabins] = useState('3');
  const [fullConfRooms, setFullConfRooms] = useState('1');
  const [fullProductionZones, setFullProductionZones] = useState('2');
  const [fullStorageAreas, setFullStorageAreas] = useState('1');
  const [fullOfficeArea, setFullOfficeArea] = useState('500');
  const [fullQualityTier, setFullQualityTier] = useState('standard');
  const [fullSpecialReq, setFullSpecialReq] = useState('');
  const [fullProgress, setFullProgress] = useState({ chunk: null, running_total: 0 });
  // iter-338z13 — Image-to-BOQ
  const [imageBase64, setImageBase64] = useState(null);
  const [imageName, setImageName] = useState('');
  const [imageInstruction, setImageInstruction] = useState('');
  // iter-366 — support PDF uploads too. When mime is application/pdf we hit
  // the same backend endpoint but with file_mime='application/pdf' so the
  // server extracts text instead of running vision on it.
  const [fileMime, setFileMime] = useState('');
  // iter-338z17 — Chat with previews (add / remove / replace items)
  const [chatInstruction, setChatInstruction] = useState('');
  const [chatting, setChatting] = useState(false);
  // iter-338z18 — audit trail viewer
  const [chatLogOpen, setChatLogOpen] = useState(false);
  const [chatLog, setChatLog] = useState([]);
  const [chatLogLoading, setChatLogLoading] = useState(false);
  // iter-359g — 7-option project type shared with "Ask AI Assistant" so both
  // AI entry points classify the project identically. Persisted (per-worksheet)
  // so re-opening the dialog restores the last selection.
  const [projectSegment, setProjectSegment] = useState(initialProjectSegment || 'residential_apartment');
  // iter-383 — Whenever the dialog opens (or the parent's project changes),
  // re-seed the segment to match the current BOQ's project type. Field stays
  // editable so admins can override on a per-request basis.
  useEffect(() => {
    if (open && initialProjectSegment) setProjectSegment(initialProjectSegment);
  }, [open, initialProjectSegment]);
  const [chatReplies, setChatReplies] = useState([]); // [{turn, reply, at}]

  // iter-359i — auto-fill project_type when segment changes.
  useEffect(() => {
    const map = {
      residential_apartment: '2/3BHK Residential Apartment',
      residential_villa: 'Residential Villa / Bungalow',
      bare_shell: 'Bare-Shell Apartment (full fit-out)',
      renovation: 'Residential Renovation',
      commercial: 'Commercial Office',
      retail_showroom: 'Retail / Showroom',
      industrial: 'Industrial Facility',
    };
    if (map[projectSegment]) {
      setFullProjectType(map[projectSegment]);
    }
  }, [projectSegment]);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  // iter-365 — pricing-model math (mirrors backend `PricingEngine`).
  // Given the pricing model + admin/client markups, derive the counterpart
  // rate from whichever rate the user is looking at. Returns the "expected"
  // client_rate given a vendor_rate (or vice-versa) so the UI can hint if
  // the two AI-generated numbers drift from the model.
  const expectedRates = (vendorRate, clientRate) => {
    const v = Number(vendorRate) || 0;
    const c = Number(clientRate) || 0;
    const am = Number(pricingContext.admin_markup_percentage) || 0;
    const cm = Number(pricingContext.client_markup_percentage) || 0;
    if (pricingContext.pricing_model === 'client_to_vendor') {
      // Client is authoritative — derive admin, then vendor.
      const admin = c / (1 + cm / 100);
      const expectedVendor = admin / (1 + am / 100);
      // Reverse: given vendor, what client would model derive?
      const derivedAdmin = v * (1 + am / 100);
      const derivedClient = derivedAdmin * (1 + cm / 100);
      return { expectedVendor, expectedClient: derivedClient };
    }
    // Default V→C: vendor is authoritative.
    const admin = v * (1 + am / 100);
    const expectedClient = admin * (1 + cm / 100);
    const derivedAdmin = c / (1 + cm / 100);
    const expectedVendor = derivedAdmin / (1 + am / 100);
    return { expectedVendor, expectedClient };
  };

  // iter-386u-ac — Auto-Rate Sync. When the user types a rate on ONE side we
  // recompute the OTHER side per the project's pricing model so the two stay
  // in lock-step. `manuallyEditedRates[i]` tracks whether the row's non-primary
  // side has been touched by hand — once touched we stop syncing that side.
  const [manualRateSides, setManualRateSides] = useState({});
  const handleRateEdit = (i, side, raw) => {
    const val = parseFloat(raw);
    const numeric = Number.isFinite(val) ? val : 0;
    const isCV = pricingContext.pricing_model === 'client_to_vendor';
    const primarySide = isCV ? 'client_rate' : 'vendor_rate';
    const derivedSide = isCV ? 'vendor_rate' : 'client_rate';
    const update = { [side]: numeric };
    // Mark manual override when user edits the derived side directly.
    if (side === derivedSide) {
      setManualRateSides((m) => ({ ...m, [`${i}:${derivedSide}`]: true }));
    }
    // Cascade only when editing the primary side AND user hasn't manually
    // overridden the derived side yet.
    if (side === primarySide && !manualRateSides[`${i}:${derivedSide}`]) {
      const current = previews[i] || {};
      const nextClient = side === 'client_rate' ? numeric : Number(current.client_rate) || 0;
      const nextVendor = side === 'vendor_rate' ? numeric : Number(current.vendor_rate) || 0;
      const { expectedVendor, expectedClient } = expectedRates(nextVendor, nextClient);
      if (isCV) update.vendor_rate = Math.round(expectedVendor * 100) / 100;
      else update.client_rate = Math.round(expectedClient * 100) / 100;
    }
    updatePreview(i, update);
  };
  // Absolute drift beyond DRIFT_PCT_THRESHOLD triggers the amber warning tint.
  const DRIFT_PCT_THRESHOLD = 5;
  const isDrift = (actual, expected) => {
    const a = Number(actual) || 0;
    const e = Number(expected) || 0;
    if (!e && !a) return false;
    if (!e) return true;
    return Math.abs(a - e) / e * 100 > DRIFT_PCT_THRESHOLD;
  };
  const fmtINR = (v) => (Number.isFinite(v) ? `₹${Math.round(v).toLocaleString('en-IN')}` : '—');

  // iter-338z4: pull custom presets whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/boq-ai-presets`, { headers });
        setCustomPresets(r.data?.presets || []);
      } catch { /* silent — dropdown falls back to standards */ }
    })();
    // iter-386u-bc — also pull the flattened materials library for the Pivot pane
    (async () => {
      try {
        const r = await axios.get(`${API}/boq-ai-presets/materials-library`, { headers });
        setMaterialsLibrary(r.data?.sections || {});
      } catch { setMaterialsLibrary({}); }
      finally { setLibraryLoaded(true); }
    })();
  }, [open]);

  const resetAll = () => { setInstruction(''); setPreviews([]); setPreset('auto'); setSaveTargetIdx(null); setSaveName(''); setChatReplies([]); };
  const close = () => { resetAll(); onOpenChange(false); };

  // iter-371-fix — when the dialog is opened in "refine mode" for a specific
  // line, seed the previews with the current line data and pre-fill an
  // instruction hint. The user gets the FULL AI Task Creator toolkit
  // (preset, segment, image/PDF context, chat-to-edit, editable card,
  // vendor/client rate suggestions) scoped to refining that one line.
  useEffect(() => {
    if (!open || !refineTargetItem) return;
    setMode('single');
    setPreset('auto');
    // Seed instruction with a nudge — user can edit before generating.
    setInstruction(`Refine this line: ${refineTargetItem.task_title || ''}\n\n(Add your changes — e.g., add soft-close hinges, bump quantity to 6, swap laminate to veneer.)`);
    // Seed the preview card so the user sees the current line before hitting Generate.
    setPreviews([{
      task_title: refineTargetItem.task_title || '',
      description: refineTargetItem.description || '',
      main_category_id: refineTargetItem.main_category_id || null,
      main_category_name: refineTargetItem.main_category_name || '',
      sub_category_id: refineTargetItem.sub_category_id || null,
      sub_category_name: refineTargetItem.sub_category_name || '',
      quantity: Number(refineTargetItem.quantity) || 1,
      unit: refineTargetItem.unit || 'NOS',
      vendor_rate: Number(refineTargetItem.vendor_rate ?? refineTargetItem.rate ?? 0),
      client_rate: Number(refineTargetItem.client_rate ?? refineTargetItem.rate ?? 0),
      gst_percent: Number(refineTargetItem.gst_percentage ?? 18),
      remarks: refineTargetItem.remarks || '',
      // iter-386u-bg — Reload build memory: hydrate pivot_inclusions +
      // lump-sum buckets so the "Line Inclusions" pane picks up right where
      // we left off. Preserves per-project rate overrides too.
      lump_sum_vendor: Number(refineTargetItem.lump_sum_vendor) || 0,
      lump_sum_client: Number(refineTargetItem.lump_sum_client) || 0,
      pivot_inclusions: Array.isArray(refineTargetItem.pivot_inclusions)
        ? refineTargetItem.pivot_inclusions.map((m) => ({
            key: m.key, label: m.label, spec: m.spec || '',
            unit: m.unit || '', category: m.category || '',
            rate: Number(m.rate) || 0,
            client_rate: Number(m.client_rate) || Number(m.rate) || 0,
            bucket: m.bucket || ((m.unit || '').toLowerCase() === (refineTargetItem.unit || '').toLowerCase() ? 'rate' : 'lump_sum'),
            override: !!m.override,
          }))
        : [],
      _refine_seed: true,
    }]);
    setChatReplies([]);
  }, [open, refineTargetItem?._editId]);

  // iter-338z17 — Chat with previews. LLM edits the current preview list
  // in place based on natural-language instructions (add missing items,
  // remove unwanted ones, replace one line with another scope).
  const handleChatEdit = async () => {
    if (previews.length === 0) { toast.error('Generate some previews first'); return; }
    if (chatInstruction.trim().length < 2) return;
    const beforeCount = previews.length;
    const userTurn = chatInstruction.trim();
    setChatting(true);
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/ai-edit-previews`,
        { previews, instruction: userTurn },
        { headers, timeout: 90000 },
      );
      const next = r.data?.previews || [];
      setPreviews(next);
      if (r.data?.pricing_context) mergePricingContext(r.data.pricing_context);
      const delta = next.length - beforeCount;
      // iter-359f — capture the natural-language reply so the UI can show it
      // in a chat-bubble style instead of a one-line toast.
      const replyText = r.data?.reply || (delta > 0
        ? `Added ${delta} item${delta !== 1 ? 's' : ''}`
        : delta < 0
          ? `Removed ${-delta} item${-delta !== 1 ? 's' : ''}`
          : 'Preview list updated');
      setChatReplies((prev) => [...prev, {
        turn: userTurn,
        reply: replyText,
        delta,
        at: new Date().toISOString(),
      }].slice(-8));
      setChatInstruction('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Edit failed');
    } finally {
      setChatting(false);
    }
  };

  // iter-338z18 — view chat history for this BOQ.
  const openChatLog = async () => {
    setChatLogOpen(true);
    setChatLogLoading(true);
    try {
      const r = await axios.get(`${API}/boq-worksheets/${worksheetId}/ai-chat-log`, { headers });
      setChatLog(r.data?.log || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load chat history');
    } finally {
      setChatLogLoading(false);
    }
  };

  // iter-338z19 — Rollback: restore the preview list to the state RIGHT
  // BEFORE the picked chat entry ran. Non-destructive — the log stays.
  const rollbackToEntry = async (entry) => {
    if (!window.confirm(`Roll back the preview list to the state before "${entry.instruction}"?\n\nThis replaces the current previews (${previews.length} items) with the ${entry.previous_count} items you had before that edit. The chat history stays intact.`)) return;
    try {
      const r = await axios.get(
        `${API}/boq-worksheets/${worksheetId}/ai-chat-log/${entry.id}/rollback`,
        { headers },
      );
      const restored = r.data?.previews || [];
      setPreviews(restored);
      toast.success(`Rolled back to ${restored.length} items (before "${entry.instruction.slice(0, 40)}${entry.instruction.length > 40 ? '…' : ''}")`);
      setChatLogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rollback failed');
    }
  };

  const handleGenerate = async () => {
    if (instruction.trim().length < 2) { toast.error('Please describe what to create'); return; }
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/ai-suggest-item`,
        { instruction: instruction.trim(), preset, project_segment: projectSegment },
        { headers },
      );
      // iter-386u-bd — AI Auto-Layering. Scan the instruction for keywords and
      // auto-select matching materials from the library so the first draft is
      // already "priced up". User can remove any chip with one click.
      const rawPreviews = r.data?.previews || [];
      const priced = rawPreviews.map((p) => autoLayerPreview(p, instruction, materialsLibrary));
      setPreviews(priced);
      // iter-386u-bi — Persist matched presets so a "Replace with preset" bar can render.
      setMatchedPresets(Array.isArray(r.data?.matched_presets) ? r.data.matched_presets : []);
      if (r.data?.pricing_context) mergePricingContext(r.data.pricing_context);
      if (!rawPreviews.length) toast.warning('No suggestion generated');
      else {
        const autoCount = priced.reduce((s, p) => s + ((p.pivot_inclusions || []).length), 0);
        if (autoCount) toast.success(`Draft ready · auto-layered ${autoCount} matching spec${autoCount !== 1 ? 's' : ''} from your library`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // iter-371-fix — Companion Suggestion generator. Called when the user
  // clicks a "+ Companion Item" chip on an existing preview card. Instead
  // of merging the chip text into the instruction, we call the same
  // ai-suggest-item endpoint with the companion label as its OWN
  // instruction and APPEND the returned preview to the current list so
  // the user ends up committing multiple line items in one go.
  const [companionBusy, setCompanionBusy] = useState({}); // { [`${srcIdx}-${chipIdx}`]: true }
  const generateCompanion = async (companionText, srcIdx, chipIdx) => {
    const key = `${srcIdx}-${chipIdx}`;
    setCompanionBusy((p) => ({ ...p, [key]: true }));
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/ai-suggest-item`,
        {
          instruction: companionText,
          preset: 'auto',
          project_segment: projectSegment,
        },
        { headers },
      );
      const added = r.data?.previews || [];
      if (!added.length) {
        toast.warning('AI could not draft a preview for that companion');
        return;
      }
      // Append — user can commit all together
      setPreviews((prev) => [...prev, ...added]);
      if (r.data?.pricing_context) mergePricingContext(r.data.pricing_context);
      toast.success(`Added companion "${companionText}" — ${previews.length + added.length} lines ready`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Companion generation failed');
    } finally {
      setCompanionBusy((p) => ({ ...p, [key]: false }));
    }
  };

  // iter-338z12 — Full Project BOQ generator (3 sequential chunk calls).
  const handleGenerateFullProject = async () => {
    const budget = parseFloat(fullBudget);
    if (!fullProjectType.trim() || !budget || budget <= 0) {
      toast.error('Enter project type and budget'); return;
    }
    setGenerating(true);
    setPreviews([]);
    setFullProgress({ chunk: null, running_total: 0 });
    try {
      let runningTotal = 0;
      let allPreviews = [];
      for (let ci = 0; ci <= 2; ci++) {
        setFullProgress({ chunk: ci, running_total: runningTotal });
        const r = await axios.post(
          `${API}/boq-worksheets/${worksheetId}/ai-generate-full-project`,
          {
            project_type: fullProjectType.trim(),
            budget,
            bedrooms: parseInt(fullBedrooms, 10) || null,
            area_sqft: parseFloat(fullAreaSqft) || null,
            notes: fullNotes.trim() || null,
            chunk_index: ci,
            running_total: runningTotal,
            project_segment: projectSegment,
            // iter-359i — segment-aware context bundle
            context: {
              location: fullLocation.trim() || null,
              floor: parseInt(fullFloor, 10) || 0,
              lift_access: fullLiftAccess,
              power_backup: fullPowerBackup, // 'yes' | 'no' | null
              home_automation: fullHomeAutomation,
              modular_kitchen: fullModularKitchen,
              bathrooms: parseInt(fullBathrooms, 10) || null,
              workstations: parseInt(fullWorkstations, 10) || null,
              cabins: parseInt(fullCabins, 10) || null,
              conf_rooms: parseInt(fullConfRooms, 10) || null,
              production_zones: parseInt(fullProductionZones, 10) || null,
              storage_areas: parseInt(fullStorageAreas, 10) || null,
              office_area_sqft: parseFloat(fullOfficeArea) || null,
              quality_tier: fullQualityTier,
              special_requirements: fullSpecialReq.trim() || null,
            },
          },
          { headers, timeout: 90000 },
        );
        const chunkPreviews = r.data?.previews || [];
        allPreviews = [...allPreviews, ...chunkPreviews];
        runningTotal = r.data?.running_total || runningTotal;
        if (r.data?.pricing_context) mergePricingContext(r.data.pricing_context);
        setPreviews([...allPreviews]);
      }
      setFullProgress({ chunk: null, running_total: runningTotal });
      const deltaPct = ((runningTotal - budget) / budget) * 100;
      toast.success(`Generated ${allPreviews.length} line items — total ₹${Math.round(runningTotal).toLocaleString('en-IN')} (${deltaPct >= 0 ? '+' : ''}${deltaPct.toFixed(1)}% vs budget)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Full project generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // iter-338z13 — File-to-BOQ upload & analyse (image OR PDF).
  const handleImagePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImage = (f.type || '').startsWith('image/');
    const isPdf = (f.type || '').toLowerCase() === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
    if (!isImage && !isPdf) { toast.error('Only image or PDF files are supported'); return; }
    if (f.size > 12 * 1024 * 1024) { toast.error('File must be under 12 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setImageBase64(String(reader.result || ''));
      setImageName(f.name);
      setFileMime(isPdf ? 'application/pdf' : (f.type || 'image/*'));
    };
    reader.readAsDataURL(f);
  };
  const handleGenerateFromImage = async () => {
    if (!imageBase64) { toast.error('Pick an image or PDF first'); return; }
    setGenerating(true);
    setPreviews([]);
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/ai-suggest-from-image`,
        {
          image_base64: imageBase64,
          instruction: imageInstruction.trim() || null,
          project_segment: projectSegment,
          file_mime: fileMime || null,
          file_name: imageName || null,
        },
        { headers, timeout: 120000 },
      );
      setPreviews(r.data?.previews || []);
      if (r.data?.pricing_context) mergePricingContext(r.data.pricing_context);
      if (!(r.data?.previews || []).length) toast.warning('No items detected in the file');
      else toast.success(`Detected ${r.data.previews.length} items — review before commit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'File analysis failed');
    } finally {
      setGenerating(false);
    }
  };

  const updatePreview = (idx, patch) => {
    setPreviews((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const removePreview = (idx) => {
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // iter-386u-bf — Roll back a pivot inclusion honoring its bucket (rate vs lump-sum).
  const removeInclusion = (idx, m, prev) => {
    const rate = Number(m.rate) || 0;
    const clientRate = Number(m.client_rate) || rate;
    const nextInclusions = (prev.pivot_inclusions || []).filter((x) => x.key !== m.key);
    const patch = {
      pivot_inclusions: nextInclusions,
      // iter-386u-bo — auto-sync description Inclusions: block
      description: syncInclusionsInDescription(prev.description || '', nextInclusions),
    };
    if (m.bucket === 'lump_sum') {
      patch.lump_sum_vendor = Math.max(0, (Number(prev.lump_sum_vendor) || 0) - rate);
      patch.lump_sum_client = Math.max(0, (Number(prev.lump_sum_client) || 0) - clientRate);
    } else {
      patch.vendor_rate = Math.max(0, (Number(prev.vendor_rate) || 0) - rate);
      patch.client_rate = Math.max(0, (Number(prev.client_rate) || 0) - clientRate);
    }
    updatePreview(idx, patch);
  };

  // iter-386u-bg — Bulk rate adjustment: shift selected chips by a global %
  // and keep the parent bucket totals in sync.
  const bulkAdjustInclusions = (idx, keys, pct, prev) => {
    if (!keys || !keys.length || !isFinite(pct) || pct === 0) return;
    const factor = 1 + pct / 100;
    let dRate = 0, dClient = 0, dLumpV = 0, dLumpC = 0;
    const nextIncl = (prev.pivot_inclusions || []).map((x) => {
      if (!keys.includes(x.key)) return x;
      const newRate = Math.max(0, Math.round((Number(x.rate) || 0) * factor));
      const newClient = Math.max(0, Math.round((Number(x.client_rate) || 0) * factor));
      const dV = newRate - (Number(x.rate) || 0);
      const dC = newClient - (Number(x.client_rate) || 0);
      if (x.bucket === 'lump_sum') { dLumpV += dV; dLumpC += dC; }
      else { dRate += dV; dClient += dC; }
      return { ...x, rate: newRate, client_rate: newClient, override: true };
    });
    updatePreview(idx, {
      pivot_inclusions: nextIncl,
      vendor_rate: Math.max(0, (Number(prev.vendor_rate) || 0) + dRate),
      client_rate: Math.max(0, (Number(prev.client_rate) || 0) + dClient),
      lump_sum_vendor: Math.max(0, (Number(prev.lump_sum_vendor) || 0) + dLumpV),
      lump_sum_client: Math.max(0, (Number(prev.lump_sum_client) || 0) + dLumpC),
    });
    toast.success(`Adjusted ${keys.length} spec${keys.length !== 1 ? 's' : ''} by ${pct > 0 ? '+' : ''}${pct}%`);
  };

  // iter-386u-bf — Edit a chip's rate/client_rate for THIS project only.
  // Adjusts the parent bucket (rate vs lump_sum) by the delta so totals stay honest.
  const editInclusionRate = (idx, key, patch, prev) => {
    const current = (prev.pivot_inclusions || []).find((x) => x.key === key);
    if (!current) return;
    const newRate = patch.rate !== undefined ? (Number(patch.rate) || 0) : (Number(current.rate) || 0);
    const newClient = patch.client_rate !== undefined ? (Number(patch.client_rate) || 0) : (Number(current.client_rate) || 0);
    const dV = newRate - (Number(current.rate) || 0);
    const dC = newClient - (Number(current.client_rate) || 0);
    const nextIncl = (prev.pivot_inclusions || []).map((x) => x.key === key ? { ...x, rate: newRate, client_rate: newClient, override: true } : x);
    const p = { pivot_inclusions: nextIncl };
    if (current.bucket === 'lump_sum') {
      p.lump_sum_vendor = Math.max(0, (Number(prev.lump_sum_vendor) || 0) + dV);
      p.lump_sum_client = Math.max(0, (Number(prev.lump_sum_client) || 0) + dC);
    } else {
      p.vendor_rate = Math.max(0, (Number(prev.vendor_rate) || 0) + dV);
      p.client_rate = Math.max(0, (Number(prev.client_rate) || 0) + dC);
    }
    updatePreview(idx, p);
  };

  const handleCommit = async () => {
    if (previews.length === 0) return;
    // iter-371-fix — Refine mode: don't create new BOQ items; instead
    // patch the parent's target line with the (possibly LLM-refined) preview.
    if (refineTargetItem && onRefineApplied) {
      // Use the LAST preview (chat-to-edit produces the newest at the end).
      const refined = previews[previews.length - 1];
      onRefineApplied({
        task_title: refined.task_title,
        description: refined.description,
        main_category_id: refined.main_category_id ?? refineTargetItem.main_category_id,
        main_category_name: refined.main_category_name ?? refineTargetItem.main_category_name,
        sub_category_id: refined.sub_category_id ?? refineTargetItem.sub_category_id,
        sub_category_name: refined.sub_category_name ?? refineTargetItem.sub_category_name,
        quantity: Number(refined.quantity) || refineTargetItem.quantity || 1,
        unit: refined.unit || refineTargetItem.unit || 'NOS',
        vendor_rate: Number(refined.vendor_rate) || 0,
        client_rate: Number(refined.client_rate) || 0,
        gst_percentage: Number(refined.gst_percent ?? refineTargetItem.gst_percentage ?? 18),
        remarks: refined.remarks || refineTargetItem.remarks || '',
        // iter-386u-bh — Preserve pivot state so the table row can render its
        // sub-breakdown and the next Refine reload reuses these chips.
        lump_sum_vendor: Number(refined.lump_sum_vendor) || 0,
        lump_sum_client: Number(refined.lump_sum_client) || 0,
        pivot_inclusions: Array.isArray(refined.pivot_inclusions) ? refined.pivot_inclusions : [],
      });
      toast.success('Line updated with refined values');
      close();
      return;
    }
    setCommitting(true);
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/ai-commit-items`,
        { items: previews },
        { headers },
      );
      const createdSubs = r.data?.created_sub_categories || [];
      toast.success(
        `Added ${r.data?.added || 0} line item${(r.data?.added || 0) !== 1 ? 's' : ''} to BOQ`
        + (createdSubs.length ? ` · Created ${createdSubs.length} sub-categor${createdSubs.length === 1 ? 'y' : 'ies'}` : ''),
      );
      onItemsCommitted && onItemsCommitted(r.data?.items || []);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  const openSaveDialog = (idx) => {
    setSaveTargetIdx(idx);
    setSaveName(previews[idx]?.task_title?.split('–')[0]?.trim()?.slice(0, 60) || '');
  };

  const submitSavePreset = async () => {
    if (saveTargetIdx == null) return;
    const name = saveName.trim();
    if (name.length < 2) { toast.error('Preset name is too short'); return; }
    const p = previews[saveTargetIdx];
    setSaving(true);
    try {
      const r = await axios.post(`${API}/boq-ai-presets`, {
        name,
        task_title: p.task_title || '',
        description: p.description || '',
        main_category_id: p.main_category_id,
        main_category_name: p.main_category_name,
        sub_category_id: p.sub_category_id,
        sub_category_name: p.sub_category_name,
        unit: p.unit || 'NOS',
        quantity: p.quantity || 1,
        vendor_rate: p.vendor_rate || 0,
        client_rate: p.client_rate || 0,
        gst_percent: p.gst_percent || 18,
        remarks: p.remarks || '',
        // iter-386u-bc — persist the pivot inclusions as the preset's materials
        // so re-opening the preset later gives an instant "one-tap" rebuild.
        materials: (p.pivot_inclusions || []).map((m) => ({
          key: m.key, label: m.label, spec: m.spec || '',
          category: m.category || '', unit: m.unit || '',
          rate: Number(m.rate) || 0, client_rate: Number(m.client_rate) || 0,
        })),
      }, { headers });
      const saved = r.data?.preset;
      toast.success(r.data?.updated ? `Preset "${name}" updated` : `Saved as preset "${name}"`);
      // Refresh preset list and auto-select the one just saved
      const list = await axios.get(`${API}/boq-ai-presets`, { headers });
      setCustomPresets(list.data?.presets || []);
      if (saved?.id) setPreset(`custom:${saved.id}`);
      setSaveTargetIdx(null); setSaveName('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomPreset = async (pid, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('Delete this custom preset?')) return;
    try {
      await axios.delete(`${API}/boq-ai-presets/${pid}`, { headers });
      setCustomPresets((prev) => prev.filter((p) => p.id !== pid));
      if (preset === `custom:${pid}`) setPreset('auto');
      toast.success('Preset removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[98vw] w-[98vw] max-h-[95vh] overflow-hidden flex flex-col" data-testid="ai-task-creator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-stone-900">
            <Sparkles size={18} className="text-violet-600" />
            {refineTargetItem ? 'Refine Line with AI' : 'AI Task Creator'}
            {refineTargetItem && (
              <span className="ml-2 text-xs font-normal text-stone-500 truncate max-w-[420px]" title={refineTargetItem.task_title}>
                — {refineTargetItem.task_title}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {refineTargetItem
              ? 'Refine an existing BOQ line with AI. Adjust the seeded preview, chat to edit, or generate a new refinement.'
              : 'Create new BOQ line items using AI. Describe what you need, optionally pick a preset and project segment, then generate a preview.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto pr-1 flex-1">
          {/* iter-359g — Project Type applies to every AI mode.
              SAME 7-option taxonomy the Ask AI Assistant page uses so both
              share the SAME backend segment map + rate memory. */}
          <div className="flex flex-wrap items-center gap-3 bg-stone-50 border border-stone-200 rounded-md px-3 py-2" data-testid="ai-task-segment-bar">
            <Label className="text-[11px] uppercase tracking-wider text-stone-600 shrink-0">Project Type</Label>
            <Select value={projectSegment} onValueChange={setProjectSegment}>
              <SelectTrigger className="bg-white text-stone-900 h-8 w-64" data-testid="ai-task-segment-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-400">Residential</SelectLabel>
                  {PROJECT_TYPES.filter(p => p.group === 'residential').map(p => (
                    <SelectItem key={p.value} value={p.value} data-testid={`ai-task-segment-${p.value}`}>
                      <span className="inline-flex items-center gap-2">
                        <p.icon size={12} className="text-stone-500" />
                        <span>{p.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-400">Commercial</SelectLabel>
                  {PROJECT_TYPES.filter(p => p.group === 'commercial').map(p => (
                    <SelectItem key={p.value} value={p.value} data-testid={`ai-task-segment-${p.value}`}>
                      <span className="inline-flex items-center gap-2">
                        <p.icon size={12} className="text-stone-500" />
                        <span>{p.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-400">Industrial</SelectLabel>
                  {PROJECT_TYPES.filter(p => p.group === 'industrial').map(p => (
                    <SelectItem key={p.value} value={p.value} data-testid={`ai-task-segment-${p.value}`}>
                      <span className="inline-flex items-center gap-2">
                        <p.icon size={12} className="text-stone-500" />
                        <span>{p.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <span className="text-[10px] text-stone-500 ml-auto">
              Materials, fixtures &amp; rates tuned to this segment — same logic used by <em>Ask AI Assistant</em>.
            </span>
          </div>

          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid w-full grid-cols-3 h-auto" data-testid="ai-task-mode-tabs">
              <TabsTrigger value="single" data-testid="ai-task-tab-single" className="text-xs">
                <Wand2 size={12} className="mr-1" /> Single / Multi Item
              </TabsTrigger>
              <TabsTrigger value="full" data-testid="ai-task-tab-full" className="text-xs">
                <Building2 size={12} className="mr-1" /> Full Project BOQ
              </TabsTrigger>
              <TabsTrigger value="image" data-testid="ai-task-tab-image" className="text-xs">
                <ImageIcon size={12} className="mr-1" /> Upload from File
              </TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="mt-3 space-y-3">
              <div className="bg-violet-50 border border-violet-200 p-3 rounded-md text-xs text-violet-900">
                Describe what you want to add. The AI reads your past BOQs to infer
                realistic rates, categories, units and descriptions. Multiple items?
                Separate with commas — e.g. <em>Wardrobe 8x6, TV Unit 6x4, Study
                table 4x2</em>. Pick a <strong>Standard Preset</strong> to inject
                trade-standard specs (e.g. Wardrobe = 18mm ply carcass + laminate
                shutters + Ebco/Enox soft-close).
              </div>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Standard Preset</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-400">Standard</SelectLabel>
                    {PRESET_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`ai-task-preset-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  {customPresets.length > 0 && (
                    <>
                      <SelectSeparator />
                      <SelectGroup>
                        <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-400">Custom</SelectLabel>
                        {customPresets.map((cp) => (
                          <SelectItem
                            key={cp.id}
                            value={`custom:${cp.id}`}
                            data-testid={`ai-task-preset-custom-${cp.id}`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <BookmarkPlus size={11} className="text-violet-500" />
                              {cp.name}
                              <button
                                type="button"
                                onClick={(e) => deleteCustomPreset(cp.id, e)}
                                className="ml-1 text-stone-400 hover:text-red-600"
                                title="Delete preset"
                                aria-label="Delete preset"
                                data-testid={`ai-task-preset-delete-${cp.id}`}
                              >
                                <X size={11} />
                              </button>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  )}
                </SelectContent>
              </Select>
              {/* iter-386u-bp — Preset Scope Picker: filterable table dialog */}
              <button
                type="button"
                onClick={() => setPresetPickerOpen(true)}
                className="mt-1 w-full text-[10px] font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded px-2 py-1 flex items-center justify-center gap-1"
                data-testid="ai-task-open-preset-picker"
              >
                <BookmarkPlus size={11} />
                Browse full preset library ({customPresets.length})
              </button>
            </div>
            <div className="col-span-6">
              <Label className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center justify-between">
                <span>Instruction</span>
                <span className="text-[9px] text-stone-400 normal-case tracking-normal">
                  {instruction.length} / 6000 · Cmd/Ctrl + Enter to generate
                </span>
              </Label>
              {/* iter-382 — swapped from single-line Input to Textarea so
                  admins can paste multi-line scope briefs (up to 6000
                  chars). Cmd/Ctrl+Enter still submits; plain Enter now
                  inserts a newline as expected. */}
              <Textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value.slice(0, 6000))}
                placeholder={
                  'e.g. Wardrobe 8x6, TV Unit 6x4\n'
                  + 'Master bedroom: full-height wardrobe with mirror; dresser 3ft; night stands\n'
                  + 'Kitchen: L-shaped modular with 5 tall units + hob cabinet\n'
                  + 'Additional notes: use MR-grade plywood, Hettich hardware, brand-neutral edge banding.'
                }
                className="bg-white text-stone-900 mt-0.5 min-h-[96px] max-h-[240px] resize-y text-sm leading-relaxed"
                data-testid="ai-task-instruction-input"
                maxLength={6000}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
              />
            </div>
            <div className="col-span-2 flex items-end">
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="bg-violet-600 hover:bg-violet-700 text-white w-full"
                data-testid="ai-task-generate-btn"
              >
                {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Wand2 size={14} className="mr-1" />}
                {generating ? 'Generating…' : 'Generate'}
                <CreditBadge toolKey={refineTargetItem ? 'ai_refine_line' : 'ai_task_creator'} variant="onDark" className="ml-1.5" />
              </Button>
            </div>
          </div>

          {/* iter-386u-bi — Matched Presets suggestion bar. Rendered ABOVE
              the library preview, only when Generate has produced matches. */}
          {matchedPresets.length > 0 && (
            <div className="border border-emerald-300 bg-emerald-50/40 rounded-lg p-2" data-testid="ai-task-matched-presets">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">Matched from your library</div>
                <span className="text-[10px] text-emerald-700">Tap any chip to REPLACE the current draft with that saved preset</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {matchedPresets.map((mp) => (
                  <button
                    key={mp.id}
                    type="button"
                    onClick={() => {
                      // Replace the entire top preview with the preset (keeping worksheet context)
                      const inclusions = (mp.materials || []).map((m) => {
                        const rate = Number(m.rate) || 0;
                        const cr = Number(m.client_rate) || rate;
                        const matches = (m.unit || '').toLowerCase() === (mp.unit || '').toLowerCase();
                        return {
                          key: m.key, label: m.label, spec: m.spec || '',
                          unit: m.unit || '', category: m.category || '',
                          rate, client_rate: cr,
                          bucket: matches ? 'rate' : 'lump_sum',
                        };
                      });
                      const lumpV = inclusions.filter((x) => x.bucket === 'lump_sum').reduce((s, x) => s + x.rate, 0);
                      const lumpC = inclusions.filter((x) => x.bucket === 'lump_sum').reduce((s, x) => s + x.client_rate, 0);
                      const replaced = {
                        task_title: mp.task_title || mp.name,
                        description: mp.description || '',
                        main_category_id: mp.main_category_id || null,
                        main_category_name: mp.main_category_name || '',
                        sub_category_id: mp.sub_category_id || null,
                        sub_category_name: mp.sub_category_name || '',
                        quantity: Number(mp.quantity) || 1,
                        unit: mp.unit || 'NOS',
                        vendor_rate: Number(mp.vendor_rate) || 0,
                        client_rate: Number(mp.client_rate) || 0,
                        gst_percent: Number(mp.gst_percent) || 18,
                        remarks: '',
                        lump_sum_vendor: lumpV, lump_sum_client: lumpC,
                        pivot_inclusions: inclusions,
                        _from_preset_id: mp.id,
                      };
                      // Replace only the first preview (users typically generate one at a time)
                      setPreviews((prev) => prev.length ? [replaced, ...prev.slice(1)] : [replaced]);
                      toast.success(`Draft replaced with preset "${mp.name}" · ${inclusions.length} spec${inclusions.length !== 1 ? 's' : ''} loaded`);
                    }}
                    className="text-xs px-2.5 py-1 rounded-full border border-emerald-400 bg-white text-emerald-800 hover:bg-emerald-100 hover:border-emerald-500 transition inline-flex items-center gap-1.5"
                    data-testid={`ai-task-matched-preset-${mp.id}`}
                    title={`${mp.description || mp.task_title || mp.name}\n${(mp.materials || []).length} material${(mp.materials || []).length !== 1 ? 's' : ''} · Match score: ${mp.match_score}`}
                  >
                    <Sparkles size={11} className="text-amber-500" />
                    <span className="font-semibold">{mp.name}</span>
                    <span className="text-[10px] text-emerald-700 font-mono">₹{(Number(mp.client_rate) || 0).toLocaleString('en-IN')}/{mp.unit || 'nos'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* iter-386u-bd — Library preview visible BEFORE any Generate click,
              so users can immediately see the specs library that will feed
              their tasks. Once previews exist, each preview owns its own
              Pivot pane bound to its inclusion list. */}
          {previews.length === 0 && (
            <div className="border border-stone-200 rounded-lg p-3 bg-white" data-testid="ai-task-library-preview">
              <div className="flex items-center justify-between mb-1.5">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Your Materials Library</div>
                  <p className="text-[10px] text-stone-500 mt-0.5">Chips shown below are pulled from every preset. Generate a task and they'll become one-click add-ons.</p>
                </div>
                <span className="text-[10px] text-stone-400">
                  {libraryLoaded ? `${Object.values(materialsLibrary).reduce((s, arr) => s + arr.length, 0)} specs · ${Object.keys(materialsLibrary).length} categories` : 'Loading…'}
                </span>
              </div>
              <PivotConfigurator
                library={materialsLibrary}
                loaded={libraryLoaded}
                inclusions={[]}
                onAdd={() => toast.info('Generate a task first — then chips move into that task\u2019s Added Specifications')}
                onRemove={() => {}}
                testIdBase="ai-task-preview-pivot"
              />
            </div>
          )}
            </TabsContent>

            <TabsContent value="full" className="mt-3 space-y-3">
              <div className="bg-violet-50 border border-violet-200 p-3 rounded-md text-xs text-violet-900">
                Give the AI a project type + budget + segment context — it will draft a
                comprehensive BOQ covering every trade for this project shape. Runs in
                3 quick passes (~30 s total). Review, tweak, and commit in one go.
              </div>

              {/* iter-359i — segment-aware Project Details form (mirrors Ask AI Assistant) */}
              {(() => {
                const segGroup = (projectSegment || '').startsWith('residential') || projectSegment === 'bare_shell' || projectSegment === 'renovation' ? 'residential'
                  : (projectSegment === 'commercial' || projectSegment === 'retail_showroom') ? 'commercial'
                  : 'industrial';
                return (
                  <div className="space-y-3">
                    {/* ── Row 1: Area · Location · Budget ── */}
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Area (Sq.ft) *</Label>
                        <Input
                          type="number"
                          value={fullAreaSqft}
                          onChange={(e) => setFullAreaSqft(e.target.value)}
                          placeholder="1500"
                          className="bg-white text-stone-900 mt-0.5"
                          data-testid="ai-task-full-area"
                        />
                      </div>
                      <div className="col-span-4">
                        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Location</Label>
                        <Input
                          value={fullLocation}
                          onChange={(e) => setFullLocation(e.target.value)}
                          placeholder="e.g. Mumbai, Delhi, Pune"
                          className="bg-white text-stone-900 mt-0.5"
                          data-testid="ai-task-full-location"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Budget (INR) *</Label>
                        <Input
                          type="number"
                          value={fullBudget}
                          onChange={(e) => setFullBudget(e.target.value)}
                          placeholder="2000000"
                          className="bg-white text-stone-900 mt-0.5"
                          data-testid="ai-task-full-budget"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Floor</Label>
                        <Input
                          type="number"
                          value={fullFloor}
                          onChange={(e) => setFullFloor(e.target.value)}
                          className="bg-white text-stone-900 mt-0.5"
                          data-testid="ai-task-full-floor"
                        />
                      </div>
                    </div>

                    {/* ── Row 2: Segment-specific details box ── */}
                    {segGroup === 'residential' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2" data-testid="ai-task-full-residential-details">
                        <div className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">Residential Details</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">Bedrooms</Label>
                            <Input type="number" value={fullBedrooms} onChange={(e) => setFullBedrooms(e.target.value)} placeholder="2" className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-bedrooms" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">Bathrooms</Label>
                            <Input type="number" value={fullBathrooms} onChange={(e) => setFullBathrooms(e.target.value)} placeholder="2" className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-bathrooms" />
                          </div>
                        </div>
                      </div>
                    )}
                    {segGroup === 'commercial' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2" data-testid="ai-task-full-commercial-details">
                        <div className="text-xs font-semibold text-amber-800">{projectSegment === 'retail_showroom' ? 'Retail / Showroom Details' : 'Commercial Details'}</div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">{projectSegment === 'retail_showroom' ? 'Display Zones' : 'Workstations'}</Label>
                            <Input type="number" value={fullWorkstations} onChange={(e) => setFullWorkstations(e.target.value)} className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-workstations" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">Cabins</Label>
                            <Input type="number" value={fullCabins} onChange={(e) => setFullCabins(e.target.value)} className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-cabins" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">Conf. Rooms</Label>
                            <Input type="number" value={fullConfRooms} onChange={(e) => setFullConfRooms(e.target.value)} className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-conf-rooms" />
                          </div>
                        </div>
                      </div>
                    )}
                    {segGroup === 'industrial' && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-2" data-testid="ai-task-full-industrial-details">
                        <div className="text-xs font-semibold text-emerald-800">Industrial Details</div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">Production Zones</Label>
                            <Input type="number" value={fullProductionZones} onChange={(e) => setFullProductionZones(e.target.value)} className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-production-zones" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">Storage Areas</Label>
                            <Input type="number" value={fullStorageAreas} onChange={(e) => setFullStorageAreas(e.target.value)} className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-storage-areas" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-600">Office Area (Sq.ft)</Label>
                            <Input type="number" value={fullOfficeArea} onChange={(e) => setFullOfficeArea(e.target.value)} className="bg-white text-stone-900 mt-0.5" data-testid="ai-task-full-office-area" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Row 3: Yes/No context questions ── */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-2 space-y-1.5" data-testid="ai-task-full-power-backup">
                        <div className="text-[11px] font-semibold text-amber-800 flex items-center gap-1">⚡ Power Backup?</div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setFullPowerBackup('yes')} data-testid="ai-task-full-power-yes"
                            className={`flex-1 py-1 text-xs rounded border ${fullPowerBackup === 'yes' ? 'bg-amber-600 border-amber-600 text-white' : 'bg-white border-stone-300 text-stone-700 hover:bg-stone-50'}`}>Yes</button>
                          <button type="button" onClick={() => setFullPowerBackup('no')} data-testid="ai-task-full-power-no"
                            className={`flex-1 py-1 text-xs rounded border ${fullPowerBackup === 'no' ? 'bg-stone-700 border-stone-700 text-white' : 'bg-white border-stone-300 text-stone-700 hover:bg-stone-50'}`}>No</button>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-md p-2">
                        <label className="inline-flex items-center gap-2 text-xs text-stone-700 cursor-pointer">
                          <input type="checkbox" checked={fullLiftAccess} onChange={(e) => setFullLiftAccess(e.target.checked)} className="h-3.5 w-3.5 accent-violet-600" data-testid="ai-task-full-lift-access" />
                          Lift Access for Material
                        </label>
                      </div>
                    </div>

                    {segGroup === 'residential' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-purple-50 border border-purple-200 rounded-md p-2 space-y-1.5" data-testid="ai-task-full-home-automation">
                          <div className="text-[11px] font-semibold text-purple-800">🏠 Home Automation?</div>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setFullHomeAutomation('yes')} data-testid="ai-task-full-ha-yes"
                              className={`flex-1 py-1 text-xs rounded border ${fullHomeAutomation === 'yes' ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-stone-300 text-stone-700'}`}>Yes</button>
                            <button type="button" onClick={() => setFullHomeAutomation('no')} data-testid="ai-task-full-ha-no"
                              className={`flex-1 py-1 text-xs rounded border ${fullHomeAutomation === 'no' ? 'bg-stone-700 border-stone-700 text-white' : 'bg-white border-stone-300 text-stone-700'}`}>No</button>
                          </div>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-md p-2 space-y-1.5" data-testid="ai-task-full-modular-kitchen">
                          <div className="text-[11px] font-semibold text-orange-800">🍽 Modular Kitchen? <span className="text-orange-500 font-normal">(separate from Carpentry)</span></div>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setFullModularKitchen('yes')} data-testid="ai-task-full-mk-yes"
                              className={`flex-1 py-1 text-xs rounded border ${fullModularKitchen === 'yes' ? 'bg-orange-600 border-orange-600 text-white' : 'bg-white border-stone-300 text-stone-700'}`}>Yes</button>
                            <button type="button" onClick={() => setFullModularKitchen('no')} data-testid="ai-task-full-mk-no"
                              className={`flex-1 py-1 text-xs rounded border ${fullModularKitchen === 'no' ? 'bg-stone-700 border-stone-700 text-white' : 'bg-white border-stone-300 text-stone-700'}`}>No</button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Quality Tier ── */}
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-stone-500 mb-1 block">Quality Tier</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { v: 'budget', label: 'Budget', range: '₹1,000-1,500/sqft' },
                          { v: 'standard', label: 'Standard', range: '₹1,500-2,500/sqft' },
                          { v: 'premium', label: 'Premium', range: '₹2,500-3,750/sqft' },
                          { v: 'luxury', label: 'Luxury', range: '₹3,750+/sqft' },
                        ].map((t) => (
                          <button
                            key={t.v}
                            type="button"
                            onClick={() => setFullQualityTier(t.v)}
                            data-testid={`ai-task-full-tier-${t.v}`}
                            className={`p-2 rounded border text-left transition-colors ${fullQualityTier === t.v ? 'border-violet-500 bg-violet-50' : 'border-stone-200 bg-white hover:bg-stone-50'}`}
                          >
                            <div className={`text-xs font-semibold ${fullQualityTier === t.v ? 'text-violet-800' : 'text-stone-800'}`}>{t.label}</div>
                            <div className={`text-[10px] font-mono ${fullQualityTier === t.v ? 'text-violet-600' : 'text-stone-500'}`}>{t.range}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── Special Requirements + Notes ── */}
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-6">
                        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Special Requirements</Label>
                        <Input
                          value={fullSpecialReq}
                          onChange={(e) => setFullSpecialReq(e.target.value)}
                          placeholder="Smart home, Italian marble..."
                          className="bg-white text-stone-900 mt-0.5"
                          data-testid="ai-task-full-special-req"
                        />
                      </div>
                      <div className="col-span-6">
                        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Notes / Overrides (optional)</Label>
                        <Input
                          value={fullNotes}
                          onChange={(e) => setFullNotes(e.target.value)}
                          placeholder="e.g. Premium finish, add walk-in wardrobe in master"
                          className="bg-white text-stone-900 mt-0.5"
                          data-testid="ai-task-full-notes"
                        />
                      </div>
                    </div>

                    {/* ── Project Type free-text (auto-derived, editable) ── */}
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-9">
                        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Project Type (auto-derived)</Label>
                        <Input
                          value={fullProjectType}
                          onChange={(e) => setFullProjectType(e.target.value)}
                          placeholder="Auto-fills from segment · edit if needed"
                          className="bg-white text-stone-900 mt-0.5"
                          data-testid="ai-task-full-type"
                        />
                      </div>
                      <div className="col-span-3 flex items-end">
                        <Button
                          type="button"
                          onClick={handleGenerateFullProject}
                          disabled={generating}
                          className="bg-violet-600 hover:bg-violet-700 text-white w-full"
                          data-testid="ai-task-full-generate-btn"
                        >
                          {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Building2 size={14} className="mr-1" />}
                          {generating ? (fullProgress.chunk !== null ? `Chunk ${fullProgress.chunk + 1}/3…` : 'Generating…') : 'Generate BOQ'}
                          <CreditBadge toolKey="ai_task_creator" variant="onDark" className="ml-1.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {fullProgress.running_total > 0 && (
                <div className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded px-2 py-1.5" data-testid="ai-task-full-progress">
                  Running total (client + GST): <span className="font-mono font-semibold text-stone-900">₹{Math.round(fullProgress.running_total).toLocaleString('en-IN')}</span>
                  {fullBudget && (() => {
                    const b = parseFloat(fullBudget);
                    const pct = ((fullProgress.running_total - b) / b) * 100;
                    return b ? (
                      <span className={`ml-2 font-medium ${Math.abs(pct) < 10 ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {pct >= 0 ? '+' : ''}{pct.toFixed(1)}% vs budget ₹{Math.round(b).toLocaleString('en-IN')}
                      </span>
                    ) : null;
                  })()}
                </div>
              )}
            </TabsContent>

            <TabsContent value="image" className="mt-3 space-y-3">
              <div className="bg-violet-50 border border-violet-200 p-3 rounded-md text-xs text-violet-900">
                Drop a site photo, sketch, reference image <strong>or a PDF</strong> (client brief, floor-plan legend, spec sheet). The AI identifies visible furniture
                and interior work + rough dimensions, and seeds 3-12 BOQ line items to review-and-commit.
              </div>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-6">
                  <Label className="text-[10px] uppercase tracking-wider text-stone-500">Reference File</Label>
                  <label
                    htmlFor="ai-task-image-input"
                    className="mt-0.5 flex items-center gap-2 cursor-pointer bg-white text-stone-700 border border-dashed border-stone-300 hover:border-violet-400 hover:bg-violet-50 rounded-md px-3 py-2 text-xs"
                    data-testid="ai-task-image-picker"
                  >
                    <Upload size={14} className="text-violet-500" />
                    {imageName || 'Click to upload an image or PDF (max 12 MB)'}
                  </label>
                  <input
                    id="ai-task-image-input"
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    onChange={handleImagePick}
                    className="hidden"
                    data-testid="ai-task-image-input"
                  />
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-stone-500">Extra Instruction</Label>
                  <Input
                    value={imageInstruction}
                    onChange={(e) => setImageInstruction(e.target.value)}
                    placeholder="e.g. 3BHK living, focus on TV wall"
                    className="bg-white text-stone-900 mt-0.5"
                    data-testid="ai-task-image-instruction"
                  />
                </div>
                <div className="col-span-2 flex items-end">
                  <Button
                    type="button"
                    onClick={handleGenerateFromImage}
                    disabled={generating || !imageBase64}
                    className="bg-violet-600 hover:bg-violet-700 text-white w-full"
                    data-testid="ai-task-image-generate-btn"
                  >
                    {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <ImageIcon size={14} className="mr-1" />}
                    {generating ? 'Analysing…' : 'Analyse'}
                    <CreditBadge toolKey="ai_task_creator" variant="onDark" className="ml-1.5" />
                  </Button>
                </div>
              </div>
              {imageBase64 && (
                fileMime === 'application/pdf' ? (
                  <div className="flex items-center gap-2 p-2 border border-stone-200 bg-stone-50 rounded text-xs text-stone-700" data-testid="ai-task-pdf-preview">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-red-100 text-red-700 rounded font-bold text-[10px]">PDF</span>
                    {imageName}
                  </div>
                ) : (
                  <img src={imageBase64} alt="Reference" className="max-h-40 rounded border border-stone-200" data-testid="ai-task-image-preview" />
                )
              )}
            </TabsContent>
          </Tabs>

          {previews.length > 0 && (
            <>
              {/* iter-359f — grand total across all previews (before commit) */}
              {(() => {
                const totals = previews.reduce((acc, p) => {
                  const q = parseFloat(p.quantity || 0);
                  const c = parseFloat(p.client_rate || 0);
                  const g = parseFloat(p.gst_percent || 0);
                  const sub = q * c;
                  acc.sub += sub;
                  acc.gst += sub * (g / 100);
                  return acc;
                }, { sub: 0, gst: 0 });
                const total = totals.sub + totals.gst;
                if (total <= 0) return null;
                return (
                  <div
                    className="flex items-center justify-between bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg px-4 py-3 shadow-sm"
                    data-testid="ai-task-previews-grand-total"
                  >
                    <div className="text-xs font-medium uppercase tracking-wider opacity-90">
                      {previews.length} preview item{previews.length !== 1 ? 's' : ''} · preview total
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="opacity-90 text-xs">Subtotal <strong className="font-mono text-white">₹{totals.sub.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></span>
                      <span className="opacity-90 text-xs">GST <strong className="font-mono text-white">₹{totals.gst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></span>
                      <span className="text-base font-bold">₹{total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                    </div>
                  </div>
                );
              })()}

              {/* iter-365 — Pricing model banner: explains which rate is
                  authoritative and reminds admins of the derived markup /
                  markdown percentages the project is configured with. */}
              {previews.length > 0 && (
                <div
                  className="text-[11px] rounded-md border border-blue-200 bg-blue-50 px-3 py-2 flex items-center justify-between gap-3"
                  data-testid="ai-task-pricing-context-banner"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className="font-semibold text-blue-900">Primary Rate:</span>
                    {/* iter-386u-bp — In-dialog primary-rate toggle. Optimistic UI flip;
                        parent BOQEditPage owns the persistence via its Pricing toggle. */}
                    <div className="inline-flex rounded-md overflow-hidden border border-blue-300" data-testid="ai-primary-rate-toggle">
                      <button
                        type="button"
                        onClick={() => {
                          setPricingContext((pc) => ({ ...pc, pricing_model: 'vendor_to_client' }));
                        }}
                        className={`px-2 py-0.5 ${pricingContext.pricing_model !== 'client_to_vendor' ? 'bg-purple-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100'}`}
                        title="Vendor drives · Client auto-recalculates"
                        data-testid="ai-primary-rate-vendor"
                      >Vendor</button>
                      <button
                        type="button"
                        onClick={() => {
                          setPricingContext((pc) => ({ ...pc, pricing_model: 'client_to_vendor' }));
                        }}
                        className={`px-2 py-0.5 border-l border-blue-300 ${pricingContext.pricing_model === 'client_to_vendor' ? 'bg-blue-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100'}`}
                        title="Client drives · Vendor auto-recalculates"
                        data-testid="ai-primary-rate-client"
                      >Client</button>
                    </div>
                    <span className="text-blue-800 text-[10px] italic">
                      {pricingContext.pricing_model === 'client_to_vendor'
                        ? 'client drives · vendor = client ÷ (1+admin%) ÷ (1+client%)'
                        : 'vendor drives · client = vendor × (1+admin%) × (1+client%)'}
                    </span>
                  </div>
                  <div className="font-mono text-blue-800 text-[11px] flex-shrink-0">
                    admin {pricingContext.admin_markup_percentage}% · client {pricingContext.client_markup_percentage}%
                  </div>
                </div>
              )}

              {/* iter-338z17 — Chat with your Project BOQ */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-md p-3 space-y-2" data-testid="ai-task-chat-panel">
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold flex items-center gap-1.5 justify-between">
                  <span className="inline-flex items-center gap-1.5">
                    <MessageSquare size={12} /> Chat with your BOQ
                  </span>
                  <button
                    type="button"
                    onClick={openChatLog}
                    className="text-[10px] text-emerald-700 hover:text-emerald-900 underline"
                    data-testid="ai-task-chat-history-btn"
                  >
                    History
                  </button>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={chatInstruction}
                    onChange={(e) => setChatInstruction(e.target.value)}
                    placeholder="e.g. Add a queen bed in each bedroom · Remove all bathroom items · Replace sofa with L-shape leather"
                    className="bg-white text-stone-900 flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && !chatting && handleChatEdit()}
                    disabled={chatting}
                    data-testid="ai-task-chat-input"
                  />
                  <Button
                    type="button"
                    onClick={handleChatEdit}
                    disabled={chatting || chatInstruction.trim().length < 2}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="ai-task-chat-apply-btn"
                  >
                    {chatting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Wand2 size={14} className="mr-1" />}
                    {chatting ? 'Editing…' : 'Apply'}
                  </Button>
                </div>
                <div className="text-[10px] text-emerald-700">
                  Add, remove, replace, or tweak items. Changes are applied to the preview only — nothing lands in the BOQ until you click Add to BOQ.
                </div>
                {/* iter-359f — conversational reply history (last 8 turns) */}
                {chatReplies.length > 0 && (
                  <div className="space-y-2 pt-2 mt-1 border-t border-emerald-200" data-testid="ai-task-chat-replies">
                    {chatReplies.map((entry, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-end">
                          <div className="bg-emerald-600 text-white text-[11px] rounded-2xl rounded-tr-sm px-3 py-1.5 max-w-[80%]" data-testid={`ai-task-chat-user-turn-${idx}`}>
                            {entry.turn}
                          </div>
                        </div>
                        <div className="flex justify-start">
                          <div className="bg-white border border-emerald-200 text-stone-800 text-[11px] rounded-2xl rounded-tl-sm px-3 py-1.5 max-w-[85%] leading-relaxed" data-testid={`ai-task-chat-reply-${idx}`}>
                            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">
                              <Sparkles size={9} /> Bella
                              {entry.delta !== 0 && (
                                <span className={`ml-1 px-1 rounded ${entry.delta > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                  {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                                </span>
                              )}
                            </div>
                            {entry.reply}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3" data-testid="ai-task-previews">
              {previews.map((p, i) => (
                <div key={i} className="bg-white border border-stone-200 rounded-lg p-3 shadow-sm" data-testid={`ai-task-preview-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] uppercase tracking-wider text-stone-500">Task Title</Label>
                      <Input
                        value={p.task_title || ''}
                        onChange={(e) => updatePreview(i, { task_title: e.target.value })}
                        className="bg-stone-50 text-stone-900 mt-0.5"
                        data-testid={`ai-task-title-${i}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openSaveDialog(i)}
                      className="text-violet-600 hover:text-violet-800 hover:bg-violet-50 mt-4"
                      title="Save as preset"
                      data-testid={`ai-task-save-preset-${i}`}
                    >
                      <BookmarkPlus size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePreview(i)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-4"
                      title="Remove"
                      data-testid={`ai-task-remove-${i}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  {saveTargetIdx === i && (
                    <div className="mb-2 flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-md px-2 py-1.5" data-testid={`ai-task-save-row-${i}`}>
                      <BookmarkPlus size={12} className="text-violet-600" />
                      <span className="text-[11px] text-violet-900 font-medium">Save this as a preset:</span>
                      <Input
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        placeholder="e.g. Studio Wardrobe v2"
                        className="h-7 text-xs bg-white text-stone-900 flex-1 max-w-xs"
                        onKeyDown={(e) => e.key === 'Enter' && submitSavePreset()}
                        data-testid={`ai-task-save-preset-name-${i}`}
                      />
                      <Button
                        size="sm"
                        onClick={submitSavePreset}
                        disabled={saving}
                        className="h-7 bg-violet-600 hover:bg-violet-700 text-white text-xs"
                        data-testid={`ai-task-save-preset-confirm-${i}`}
                      >
                        {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSaveTargetIdx(null); setSaveName(''); }}
                        className="h-7 text-xs text-stone-500"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-6 gap-2 text-xs">
                    <div className="col-span-4">
                      <CategoryDropdown
                        mainId={p.main_category_id}
                        mainName={p.main_category_name}
                        subId={p.sub_category_id}
                        subName={p.sub_category_name}
                        onChange={(next) => updatePreview(i, next)}
                        testIdPrefix={`ai-task-cat-${i}`}
                      />
                      {p.will_create_sub_category && p.proposed_sub_code && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                          data-testid={`ai-task-new-sub-chip-${i}`}
                          title="A new sub-category will be created when you commit"
                        >
                          <FolderPlus size={10} />
                          New sub-category will be created: <span className="font-mono">{p.proposed_sub_code}</span> – {p.sub_category_name}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-stone-500">Qty</Label>
                      <Input
                        type="number"
                        value={p.quantity}
                        onChange={(e) => updatePreview(i, { quantity: parseFloat(e.target.value) || 0 })}
                        className="bg-stone-50 text-stone-900 mt-0.5"
                        data-testid={`ai-task-qty-${i}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-stone-500">Unit</Label>
                      <div className="mt-0.5">
                        <UnitSelect
                          value={p.unit || 'Sq.ft'}
                          onChange={(v) => updatePreview(i, { unit: v })}
                          testId={`ai-task-unit-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                  {p.rate_source && (
                    <div className="mt-2" data-testid={`ai-task-rate-source-${i}`}>
                      {p.rate_source === 'ai_estimate' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">No history — AI estimate</span>
                      ) : p.rate_source === 'library' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">Rate from your library{p.rate_anchor?.preset_name ? ` · ${p.rate_anchor.preset_name}` : ''}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">Rate from {p.history_hint?.match_count || ''} confirmed BOQ{p.history_hint?.match_count === 1 ? '' : 's'} (newest weighted)</span>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    {(() => {
                      // Fix 8: when the rate came from org history / library, "Model expects" shows THAT anchor.
                      const anchor = p.rate_anchor && p.rate_anchor.source !== 'ai_estimate' ? p.rate_anchor : null;
                      const modelRates = expectedRates(p.vendor_rate, p.client_rate);
                      const expectedVendor = anchor ? Number(anchor.vendor_rate) : modelRates.expectedVendor;
                      const expectedClient = anchor ? Number(anchor.client_rate) : modelRates.expectedClient;
                      const vDrift = isDrift(p.vendor_rate, expectedVendor);
                      const cDrift = isDrift(p.client_rate, expectedClient);
                      const isCV = pricingContext.pricing_model === 'client_to_vendor';
                      const anchorLabel = anchor ? (anchor.source === 'library' ? ' (library)' : ' (history avg)') : '';
                      return (
                        <>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1">
                              Vendor Rate ₹
                              {!isCV && <span className="text-[9px] normal-case text-emerald-700 font-semibold" title="Editable side per this project's pricing model">· primary</span>}
                            </Label>
                            <Input
                              type="number"
                              value={p.vendor_rate}
                              onChange={(e) => handleRateEdit(i, 'vendor_rate', e.target.value)}
                              className={`text-stone-900 mt-0.5 ${vDrift ? 'bg-amber-50 border-amber-300' : 'bg-stone-50'}`}
                              data-testid={`ai-task-vendor-rate-${i}`}
                            />
                            {(anchor || (isCV && Number(p.client_rate) > 0)) && (
                              <p className={`text-[10px] mt-0.5 ${vDrift ? 'text-amber-700 font-semibold' : 'text-stone-500'}`}
                                 data-testid={`ai-task-vendor-hint-${i}`}
                                 title={`Model: ${pricingContext.pricing_model}, admin ${pricingContext.admin_markup_percentage}% + client ${pricingContext.client_markup_percentage}%`}>
                                {vDrift ? '⚠ ' : ''}Model expects {fmtINR(expectedVendor)}{anchorLabel}
                              </p>
                            )}
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1">
                              Client Rate ₹
                              {isCV && <span className="text-[9px] normal-case text-emerald-700 font-semibold" title="Editable side per this project's pricing model">· primary</span>}
                            </Label>
                            <Input
                              type="number"
                              value={p.client_rate}
                              onChange={(e) => handleRateEdit(i, 'client_rate', e.target.value)}
                              className={`text-stone-900 mt-0.5 ${cDrift ? 'bg-amber-50 border-amber-300' : 'bg-stone-50'}`}
                              data-testid={`ai-task-client-rate-${i}`}
                            />
                            {(anchor || (!isCV && Number(p.vendor_rate) > 0)) && (
                              <p className={`text-[10px] mt-0.5 ${cDrift ? 'text-amber-700 font-semibold' : 'text-stone-500'}`}
                                 data-testid={`ai-task-client-hint-${i}`}
                                 title={`Model: ${pricingContext.pricing_model}, admin ${pricingContext.admin_markup_percentage}% + client ${pricingContext.client_markup_percentage}%`}>
                                {cDrift ? '⚠ ' : ''}Model expects {fmtINR(expectedClient)}{anchorLabel}
                              </p>
                            )}
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-stone-500">GST %</Label>
                            <Input
                              type="number"
                              value={p.gst_percent}
                              onChange={(e) => updatePreview(i, { gst_percent: parseFloat(e.target.value) || 0 })}
                              className="bg-stone-50 text-stone-900 mt-0.5"
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-stone-500">Description</Label>
                    <Textarea
                      rows={12}
                      value={p.description || ''}
                      onChange={(e) => updatePreview(i, { description: e.target.value })}
                      className="bg-stone-50 text-stone-900 mt-0.5 text-xs font-mono whitespace-pre-wrap"
                      data-testid={`ai-task-desc-${i}`}
                    />
                    {/* iter-386u-bf — Unit-aware layering. If material unit
                        matches the task unit, cost lands in per-unit rates.
                        If unit differs (e.g. Nos accessory on a Sq.ft task),
                        cost lands in lump_sum_* so line total math stays
                        correct: (qty * rate) + lump_sum. */}
                    <PivotConfigurator
                      library={materialsLibrary}
                      loaded={libraryLoaded}
                      inclusions={p.pivot_inclusions || []}
                      taskUnit={p.unit || 'Sq.ft'}
                      onAdd={(m) => {
                        const already = (p.pivot_inclusions || []).some((x) => x.key === m.key);
                        if (already) { toast.info(`${m.label} already in this task`); return; }
                        const rate = Number(m.rate) || 0;
                        const clientRate = Number(m.client_rate) || rate;
                        const materialUnit = (m.unit || '').trim();
                        const taskUnit = (p.unit || '').trim();
                        const unitMatches = materialUnit && taskUnit && materialUnit.toLowerCase() === taskUnit.toLowerCase();
                        const inclusion = {
                          key: m.key, label: m.label, spec: m.spec || '',
                          unit: materialUnit, category: m.category || '',
                          rate, client_rate: clientRate,
                          bucket: unitMatches ? 'rate' : 'lump_sum',
                        };
                        if (unitMatches) {
                          const nextIncl = [...(p.pivot_inclusions || []), inclusion];
                          updatePreview(i, {
                            vendor_rate: (Number(p.vendor_rate) || 0) + rate,
                            client_rate: (Number(p.client_rate) || 0) + clientRate,
                            pivot_inclusions: nextIncl,
                            // iter-386u-bo — auto-sync description Inclusions: block
                            description: syncInclusionsInDescription(p.description || '', nextIncl),
                          });
                          toast.success(`+ ${m.label} added to per-unit rate (V ₹${rate} · C ₹${clientRate})`);
                        } else {
                          const nextIncl = [...(p.pivot_inclusions || []), inclusion];
                          updatePreview(i, {
                            lump_sum_vendor: (Number(p.lump_sum_vendor) || 0) + rate,
                            lump_sum_client: (Number(p.lump_sum_client) || 0) + clientRate,
                            pivot_inclusions: nextIncl,
                            // iter-386u-bo — auto-sync description Inclusions: block
                            description: syncInclusionsInDescription(p.description || '', nextIncl),
                          });
                          toast.success(`+ ${m.label} added as lump-sum (${materialUnit || 'no-unit'} \u2260 ${taskUnit || 'task'}: V ₹${rate} · C ₹${clientRate})`);
                        }
                      }}
                      onRemove={(m) => removeInclusion(i, m, p)}
                      testIdBase={`ai-task-pivot-${i}`}
                    />
                    {/* iter-386u-bf — dual-rate, sortable, editable chips */}
                    <AddedSpecsRow
                      inclusions={p.pivot_inclusions || []}
                      pricingModel={pricingContext.pricing_model}
                      onRemove={(m) => removeInclusion(i, m, p)}
                      onEditRate={(key, patch) => editInclusionRate(i, key, patch, p)}
                      onReorder={(next) => updatePreview(i, { pivot_inclusions: next })}
                      onBulkAdjust={(keys, pct) => bulkAdjustInclusions(i, keys, pct, p)}
                      testIdBase={`ai-task-added-${i}`}
                    />
                    {/* iter-386u-bf — Line total breakdown chip */}
                    {(Number(p.lump_sum_vendor) || Number(p.lump_sum_client)) ? (
                      <div className="mt-1 flex items-center justify-end gap-3 text-[10px] text-stone-600" data-testid={`ai-task-lump-summary-${i}`}>
                        <span>Line total = (qty × rate) + lump-sum</span>
                        <span className="font-mono text-stone-800">
                          Lump: ₹{(Number(p.lump_sum_vendor) || 0).toLocaleString('en-IN')} (V) · ₹{(Number(p.lump_sum_client) || 0).toLocaleString('en-IN')} (C)
                        </span>
                      </div>
                    ) : null}
                    {/* iter-386u-bc — "Save this Build as New Preset" one-click loop */}
                    <div className="mt-2 flex items-center justify-end">
                      <Button size="sm" variant="outline"
                        onClick={() => openSaveDialog(i)}
                        className="text-[11px]"
                        data-testid={`ai-task-save-build-${i}`}
                      >
                        <BookmarkPlus size={12} className="mr-1" />
                        Save this build as new preset
                      </Button>
                    </div>
                  </div>
                  {/* iter-386u-bg — Live line-total with full formula display.
                      Math: (qty × client_rate) + lump_sum_client, then + GST. */}
                  {(() => {
                    const q = parseFloat(p.quantity || 0);
                    const c = parseFloat(p.client_rate || 0);
                    const g = parseFloat(p.gst_percent || 0);
                    const lump = parseFloat(p.lump_sum_client || 0);
                    const preGst = q * c + lump;
                    const gstAmt = preGst * (g / 100);
                    const total = preGst + gstAmt;
                    if (preGst <= 0) return null;
                    const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                    return (
                      <div className="mt-2 space-y-1" data-testid={`ai-task-preview-total-${i}`}>
                        {/* Basic Total (Pre-GST) */}
                        <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded px-3 py-1.5 text-[11px]">
                          <span className="font-mono text-stone-700" data-testid={`ai-task-basic-total-${i}`}>
                            Basic Total (pre-GST): {q.toLocaleString('en-IN')} {p.unit || ''} × {money(c)}
                            {lump > 0 && <> + {money(lump)} <span className="text-amber-700">(lump-sum)</span></>}
                            <span className="text-stone-500"> = </span>
                            <strong className="text-stone-900">{money(preGst)}</strong>
                          </span>
                        </div>
                        {/* Full Line Total with GST */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-[11px]">
                          <span className="text-emerald-800 font-mono" data-testid={`ai-task-formula-${i}`}>
                            {money(preGst)} + {g}% GST {money(gstAmt)}
                            <span className="text-stone-500"> = </span>
                            <strong className="text-stone-900">{money(total)}</strong>
                          </span>
                          <span className="text-emerald-900 font-semibold ml-auto">
                            Line Total <strong className="font-mono text-emerald-900">{money(total)}</strong>
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  {p.source_hits > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // iter-386u-bs — Auto-Fill Link: click historical hint
                        // to hard-anchor Qty / Unit / Client Rate from the median
                        const h = p.history_hint || {};
                        const patch = {};
                        if (h.median_quantity) patch.quantity = Number(h.median_quantity);
                        if (h.median_unit) patch.unit = h.median_unit;
                        if (h.median_client_rate) patch.client_rate = Number(h.median_client_rate);
                        if (h.median_vendor_rate) patch.vendor_rate = Number(h.median_vendor_rate);
                        if (Object.keys(patch).length === 0) {
                          if (h.median_vendor_rate) patch.vendor_rate = Number(h.median_vendor_rate);
                        }
                        if (Object.keys(patch).length > 0) {
                          updatePreview(i, patch);
                          toast.success(`Auto-filled from ${h.match_count ?? p.source_hits} historical items · median ₹${Math.round(h.median_vendor_rate || 0).toLocaleString('en-IN')}`);
                        }
                      }}
                      className="text-[10px] text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 hover:border-emerald-400 rounded px-1.5 py-0.5 mt-1 inline-flex items-center gap-1 cursor-pointer transition"
                      data-testid={`preview-history-hint-${i}`}
                      title="Click to auto-fill Qty, Unit and Rate from historical median"
                    >
                      <Sparkles size={9} className="text-emerald-600" />
                      <span className="font-medium">Based on {p.history_hint?.match_count ?? p.source_hits} similar item{(p.history_hint?.match_count ?? p.source_hits) !== 1 ? 's' : ''} in your past BOQs</span>
                      {p.history_hint?.median_vendor_rate ? (
                        <span className="text-[9px] text-emerald-600 font-bold">
                          · median ₹{Math.round(p.history_hint.median_vendor_rate).toLocaleString('en-IN')} <span className="underline">use</span>
                        </span>
                      ) : null}
                    </button>
                  )}
                  {/* iter-371-fix: Companion Suggestions — clicking a chip
                      calls the AI immediately to draft that companion as
                      its OWN preview card (appended to the list). The user
                      then commits multiple lines in one go instead of
                      overwriting the current instruction. */}
                  {Array.isArray(p.companion_items) && p.companion_items.length > 0 && (
                    <div className="mt-2 border-t border-stone-200 pt-2" data-testid={`ai-task-companion-${i}`}>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
                        <Sparkles size={10} className="text-purple-600" /> Also consider — click to add as separate line
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {p.companion_items.map((c, ci) => {
                          const key = `${i}-${ci}`;
                          const busy = !!companionBusy[key];
                          return (
                            <button
                              type="button"
                              key={ci}
                              disabled={busy}
                              onClick={() => generateCompanion(c, i, ci)}
                              className="px-2 py-0.5 text-[11px] rounded-full bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1"
                              data-testid={`ai-task-companion-chip-${i}-${ci}`}
                              title="Generate this companion item as a new line"
                            >
                              {busy ? <Loader2 size={9} className="animate-spin" /> : '+'} {c}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t mt-2">
          <Button variant="outline" onClick={close} disabled={committing}>Cancel</Button>
          <Button
            onClick={handleCommit}
            disabled={committing || previews.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="ai-task-commit-btn"
          >
            {committing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
            {refineTargetItem
              ? 'Apply Refinement to Line'
              : `Add ${previews.length > 0 ? `${previews.length} ` : ''}to BOQ`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      {/* iter-338z18 — chat history / audit trail dialog */}
      <Dialog open={chatLogOpen} onOpenChange={setChatLogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[80vh] overflow-hidden flex flex-col" data-testid="ai-task-chat-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-900">
              <MessageSquare size={16} className="text-emerald-600" />
              Chat history for this BOQ
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 mt-2 border border-stone-200 rounded-md">
            {chatLogLoading ? (
              <div className="flex items-center gap-2 text-sm text-stone-500 py-10 justify-center">
                <Loader2 size={16} className="animate-spin" /> Loading history…
              </div>
            ) : chatLog.length === 0 ? (
              <div className="text-center py-10 text-sm text-stone-500">
                No chat edits yet. Instructions you send to Chat with your BOQ will appear here.
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {chatLog.map((entry) => (
                  <div key={entry.id} className="px-3 py-2.5" data-testid={`ai-task-chat-log-entry-${entry.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm text-stone-900 flex-1 min-w-0">
                        &ldquo;{entry.instruction}&rdquo;
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${entry.delta > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : entry.delta < 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-stone-50 text-stone-600 border-stone-200'}`}
                        title="Change in preview count"
                      >
                        {entry.delta > 0 ? `+${entry.delta}` : entry.delta < 0 ? entry.delta : '±0'}
                      </span>
                    </div>
                    <div className="text-[11px] text-stone-500 mt-1 flex items-center gap-2 justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span>{new Date(entry.timestamp).toLocaleString()}</span>
                        {entry.user_email && (
                          <>
                            <span className="text-stone-300">·</span>
                            <span className="truncate">{entry.user_email}</span>
                          </>
                        )}
                        <span className="text-stone-300">·</span>
                        <span className="font-mono whitespace-nowrap">{entry.previous_count} → {entry.new_count} items</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => rollbackToEntry(entry)}
                        className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-50 px-2 py-0.5 rounded"
                        data-testid={`ai-task-chat-rollback-${entry.id}`}
                        title="Restore the preview list to the state before this edit"
                      >
                        <RotateCcw size={11} /> Rollback
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setChatLogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter-386u-bp — Preset Scope Picker table */}
      <Dialog open={presetPickerOpen} onOpenChange={setPresetPickerOpen}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="preset-scope-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Preset Library</DialogTitle>
            <DialogDescription>
              Pick a saved template to instantly create a new task with its scope, materials and pricing pre-filled.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-shrink-0 py-2">
            <Input
              placeholder="Filter by name, category or description…"
              value={presetPickerQuery}
              onChange={(e) => setPresetPickerQuery(e.target.value)}
              className="h-9 text-sm"
              data-testid="preset-picker-search"
            />
          </div>
          <div className="flex-1 overflow-y-auto border border-stone-200 rounded-md">
            {customPresets.length === 0 ? (
              <div className="p-8 text-center text-sm text-stone-500">
                No saved presets yet. Save a build via the &ldquo;Save this build as new preset&rdquo; button on any row.
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="preset-picker-table">
                <thead className="bg-stone-50 border-b border-stone-200 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-stone-700">Name</th>
                    <th className="text-left px-3 py-2 font-semibold text-stone-700">Category</th>
                    <th className="text-left px-3 py-2 font-semibold text-stone-700">Unit</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-700">Vendor ₹</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-700">Client ₹</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-700">Inclusions</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customPresets
                    .filter((cp) => {
                      const q = presetPickerQuery.trim().toLowerCase();
                      if (!q) return true;
                      return `${cp.name || ''} ${cp.task_title || ''} ${cp.main_category_name || ''} ${cp.description || ''}`.toLowerCase().includes(q);
                    })
                    .map((cp) => (
                      <tr key={cp.id} className="border-b border-stone-100 hover:bg-violet-50/40" data-testid={`preset-picker-row-${cp.id}`}>
                        <td className="px-3 py-2 font-semibold text-stone-900">{cp.name}</td>
                        <td className="px-3 py-2 text-stone-600">{cp.main_category_name || '—'}</td>
                        <td className="px-3 py-2 text-stone-600 font-mono">{cp.unit || 'NOS'}</td>
                        <td className="px-3 py-2 text-right font-mono text-stone-800">{(Number(cp.vendor_rate) || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right font-mono text-stone-800">{(Number(cp.client_rate) || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right text-stone-500">{(cp.materials || []).length}</td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] border-violet-300 text-violet-700 hover:bg-violet-50"
                            onClick={() => {
                              const inclusions = (cp.materials || []).map((m) => ({
                                key: m.key || m.label,
                                label: m.label,
                                spec: m.spec || '',
                                unit: m.unit || '',
                                category: m.category || '',
                                rate: Number(m.rate) || 0,
                                client_rate: Number(m.client_rate) || Number(m.rate) || 0,
                                bucket: 'rate',
                              }));
                              const seed = {
                                task_title: cp.task_title || cp.name,
                                description: cp.description || '',
                                main_category_id: cp.main_category_id,
                                main_category_name: cp.main_category_name,
                                sub_category_id: cp.sub_category_id,
                                sub_category_name: cp.sub_category_name,
                                unit: cp.unit || 'NOS',
                                quantity: Number(cp.quantity) || 1,
                                vendor_rate: Number(cp.vendor_rate) || 0,
                                client_rate: Number(cp.client_rate) || 0,
                                gst_percent: Number(cp.gst_percent) || 18,
                                lump_sum_vendor: Number(cp.lump_sum_vendor) || 0,
                                lump_sum_client: Number(cp.lump_sum_client) || 0,
                                pivot_inclusions: inclusions,
                              };
                              seed.description = syncInclusionsInDescription(seed.description, inclusions);
                              setPreviews((prev) => [...prev, seed]);
                              toast.success(`Added "${cp.name}" to previews. Adjust and commit.`);
                              setPresetPickerOpen(false);
                            }}
                            data-testid={`preset-picker-use-${cp.id}`}
                          >
                            <Plus size={11} className="mr-1" />
                            Use
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <div className="text-[11px] text-stone-500">
              {customPresets.length} preset{customPresets.length !== 1 ? 's' : ''} in your library
            </div>
            <Button variant="outline" onClick={() => setPresetPickerOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// iter-386u-bf — Highlighted "Added Specifications" chips with:
//  · Dual rate display (V | C)
//  · Inline click-to-edit rate + client_rate per chip (this-project override)
//  · Drag-and-drop reordering via dnd-kit
function AddedSpecsRow({ inclusions, onRemove, onEditRate, onReorder, onBulkAdjust, testIdBase, pricingModel }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [selected, setSelected] = useState(new Set());
  const [pct, setPct] = useState('');
  if (!inclusions || inclusions.length === 0) return null;
  const totalV = inclusions.reduce((s, x) => s + (Number(x.rate) || 0), 0);
  const totalC = inclusions.reduce((s, x) => s + (Number(x.client_rate) || 0), 0);

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const applyBulk = () => {
    const p = parseFloat(pct);
    if (!isFinite(p) || p === 0) return;
    onBulkAdjust([...selected], p);
    setSelected(new Set()); setPct('');
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = inclusions.findIndex((x) => x.key === active.id);
    const newIdx = inclusions.findIndex((x) => x.key === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(arrayMove(inclusions, oldIdx, newIdx));
  };

  return (
    <div className="mt-2 border border-violet-300 rounded-md bg-violet-50/60 p-2" data-testid={testIdBase}>
      <div className="flex items-center justify-between mb-1.5 gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-violet-800 font-semibold">Added Specifications ({inclusions.length})</span>
        <span className="text-[10px] font-mono text-violet-900">Boost: ₹{totalV.toLocaleString('en-IN')} (V) · ₹{totalC.toLocaleString('en-IN')} (C)</span>
      </div>
      {selected.size > 0 && (
        <div className="mb-2 p-1.5 rounded bg-violet-100 border border-violet-300 flex items-center gap-2 text-[11px]" data-testid={`${testIdBase}-bulk-bar`}>
          <span className="font-semibold text-violet-900">{selected.size} selected</span>
          <input type="number" placeholder="+10 or -5" value={pct} onChange={(e) => setPct(e.target.value)}
            className="w-24 border border-violet-300 rounded px-1 py-0.5 text-right"
            data-testid={`${testIdBase}-bulk-pct`} />
          <span>%</span>
          <button type="button" onClick={applyBulk}
            className="ml-1 px-2 py-0.5 rounded bg-violet-600 text-white text-[11px] font-semibold hover:bg-violet-700"
            data-testid={`${testIdBase}-bulk-apply`}>
            Apply
          </button>
          <button type="button" onClick={() => setSelected(new Set())}
            className="text-[11px] text-stone-500 hover:text-stone-800 ml-1"
            data-testid={`${testIdBase}-bulk-clear`}>
            Clear
          </button>
          <span className="ml-auto text-[10px] text-violet-700 italic">Percentage adjusts both Vendor and Client rates on selected chips.</span>
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={inclusions.map((x) => x.key)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-1.5">
            {inclusions.map((m) => (
              <AddedSpecChip key={m.key} m={m}
                selected={selected.has(m.key)}
                onToggleSelect={() => toggle(m.key)}
                onRemove={onRemove}
                onEditRate={onEditRate}
                pricingModel={pricingModel}
                testIdBase={testIdBase}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function AddedSpecChip({ m, selected, onToggleSelect, onRemove, onEditRate, testIdBase, pricingModel }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState(m.rate);
  const [clientRate, setClientRate] = useState(m.client_rate);
  useEffect(() => { setRate(m.rate); setClientRate(m.client_rate); }, [m.rate, m.client_rate]);
  const commit = () => {
    onEditRate(m.key, { rate: Number(rate) || 0, client_rate: Number(clientRate) || 0 });
    setEditing(false);
  };
  // iter-386u-bh — Show the rate matching the current pricing model as the
  // primary chip label. Secondary rate follows in a muted tone.
  const clientFirst = pricingModel === 'client_to_vendor';
  return (
    <div ref={setNodeRef} style={style}
      className={`inline-flex items-center gap-2 text-[11px] px-2 py-1 rounded bg-violet-100 border ${m.bucket === 'lump_sum' ? 'border-amber-400 bg-amber-50' : 'border-violet-300'} ${selected ? 'ring-2 ring-violet-500' : ''}`}
      data-testid={`${testIdBase}-chip-${m.key}`}
      title={m.spec || m.label}
    >
      <input type="checkbox" checked={selected} onChange={onToggleSelect} className="accent-violet-600" data-testid={`${testIdBase}-chip-select-${m.key}`} />
      <span {...attributes} {...listeners} className="cursor-grab text-violet-500 hover:text-violet-800 select-none px-1" title="Drag to reorder">⋮⋮</span>
      <span className="font-semibold text-stone-900">[{m.label}]</span>
      <span className="text-[9px] font-mono text-stone-500">{m.unit || '—'}</span>
      {m.bucket === 'lump_sum' && (
        <span className="text-[9px] uppercase tracking-wider text-amber-800 bg-amber-100 px-1 rounded" title="Different unit — added as lump sum, not per-unit rate">L.S</span>
      )}
      {m.override && (
        <span className="text-[9px] uppercase tracking-wider text-violet-800 bg-violet-200 px-1 rounded" title="Rate overridden for this project">Override</span>
      )}
      {editing ? (
        <span className="inline-flex items-center gap-1">
          Vendor <input type="number" value={rate} onChange={(e) => setRate(e.target.value)}
            className="w-16 text-[11px] border border-stone-300 rounded px-1 py-0.5 text-right" data-testid={`${testIdBase}-edit-vendor-${m.key}`} />
          Client <input type="number" value={clientRate} onChange={(e) => setClientRate(e.target.value)}
            className="w-16 text-[11px] border border-stone-300 rounded px-1 py-0.5 text-right" data-testid={`${testIdBase}-edit-client-${m.key}`} />
          <button type="button" onClick={commit} className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-900" data-testid={`${testIdBase}-edit-save-${m.key}`}>Save</button>
        </span>
      ) : (
        <button type="button" onClick={() => setEditing(true)}
          className="text-violet-700 font-mono hover:text-violet-900 hover:underline"
          data-testid={`${testIdBase}-rate-${m.key}`}
          title="Click to override rate for this project only"
        >
          {clientFirst ? (
            <>₹{(Number(m.client_rate) || 0).toLocaleString('en-IN')} (C) <span className="text-stone-400">|</span> <span className="text-stone-500">₹{(Number(m.rate) || 0).toLocaleString('en-IN')} (V)</span></>
          ) : (
            <>₹{(Number(m.rate) || 0).toLocaleString('en-IN')} (V) <span className="text-stone-400">|</span> ₹{(Number(m.client_rate) || 0).toLocaleString('en-IN')} (C)</>
          )}
        </button>
      )}
      <button type="button" onClick={() => onRemove(m)} className="text-violet-500 hover:text-rose-600 leading-none pl-1" data-testid={`${testIdBase}-remove-${m.key}`}>×</button>
    </div>
  );
}


// iter-386u-bc — Pivot Task Configurator (two-pane library ↔ inclusions).
function PivotConfigurator({ library, loaded, inclusions, onAdd, onRemove, testIdBase, taskUnit }) {
  const sections = Object.entries(library || {});
  const inclusionKeys = new Set((inclusions || []).map((x) => x.key));
  const inclusionTotal = (inclusions || []).reduce((s, x) => s + (Number(x.rate) || 0), 0);

  if (!loaded) {
    return (
      <div className="mt-2 border border-stone-200 rounded-md p-3 bg-white text-[11px] text-stone-400 italic" data-testid={`${testIdBase}-loading`}>
        Loading materials library…
      </div>
    );
  }
  if (sections.length === 0) {
    return (
      <div className="mt-2 border border-dashed border-violet-300 rounded-md p-3 bg-violet-50/40 text-[11px] text-violet-800" data-testid={`${testIdBase}-empty`}>
        No materials in your library yet. Head to <strong>Studio Tools → AI Preset Library</strong>, open any preset, and add a few rows under &quot;Materials &amp; Technical Specs&quot;. They&apos;ll appear here instantly.
      </div>
    );
  }

  return (
    <div className="mt-2 grid md:grid-cols-2 gap-2" data-testid={testIdBase}>
      {/* Left: Available */}
      <div className="border border-stone-200 rounded-md bg-white overflow-hidden">
        <div className="bg-stone-50 px-2.5 py-1 text-[10px] uppercase tracking-wider text-stone-500 font-semibold border-b border-stone-200 flex items-center justify-between">
          <span>Library · Available specs</span>
          <span className="text-[9px] font-normal normal-case text-stone-400">click to add →</span>
        </div>
        <div className="max-h-64 overflow-y-auto text-xs divide-y divide-stone-100">
          {sections.map(([cat, items]) => {
            const rest = items.filter((it) => !inclusionKeys.has(it.key));
            if (!rest.length) return null;
            return (
              <div key={cat}>
                <div className="px-2 py-1 bg-stone-50/70 text-[9px] uppercase tracking-wider text-stone-400 font-semibold">{cat}</div>
                {rest.map((it) => {
                  const isMismatch = taskUnit && it.unit && (it.unit.toLowerCase() !== taskUnit.toLowerCase());
                  return (
                    <button
                      key={it.key}
                      type="button"
                      onClick={() => onAdd(it)}
                      className="w-full text-left px-2 py-1.5 hover:bg-emerald-50 flex items-start gap-2 group"
                      data-testid={`${testIdBase}-avail-${it.key}`}
                      title={it.spec || it.label}
                    >
                      <span className="text-emerald-600 text-[10px] flex-shrink-0 mt-0.5 group-hover:text-emerald-700">+</span>
                      <span className="flex-1 min-w-0">
                        <span className="text-stone-800 font-medium">{it.label}</span>
                        <span className="ml-1 text-[9px] font-mono text-stone-400">{it.unit || ''}</span>
                        {isMismatch && (
                          <span className="ml-1 text-[9px] uppercase text-amber-800 bg-amber-100 px-1 rounded" title={`Task unit "${taskUnit}" ≠ "${it.unit}" — will land in lump-sum`}>L.S</span>
                        )}
                        {it.spec && <div className="text-[9px] text-stone-400 truncate">{it.spec}</div>}
                      </span>
                      <span className="text-[10px] font-mono text-stone-600 flex-shrink-0 text-right leading-tight">
                        <div>₹{(Number(it.rate) || 0).toLocaleString('en-IN')} (V)</div>
                        <div className="text-emerald-700">₹{(Number(it.client_rate) || it.rate || 0).toLocaleString('en-IN')} (C)</div>
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {/* Right: Inclusions */}
      <div className="border border-emerald-300 rounded-md bg-white overflow-hidden">
        <div className="bg-emerald-50 px-2.5 py-1 text-[10px] uppercase tracking-wider text-emerald-800 font-semibold border-b border-emerald-200 flex items-center justify-between">
          <span>Line inclusions ({(inclusions || []).length})</span>
          <span className="text-[10px] font-mono text-emerald-900">₹{inclusionTotal.toLocaleString('en-IN')}</span>
        </div>
        <div className="max-h-64 overflow-y-auto text-xs divide-y divide-stone-100">
          {(inclusions || []).length === 0 && (
            <div className="p-3 text-center text-[10px] text-stone-400 italic">Click any spec on the left to layer it in. Description text and rates update live.</div>
          )}
          {(inclusions || []).map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => onRemove(it)}
              className="w-full text-left px-2 py-1.5 hover:bg-rose-50 flex items-start gap-2 group"
              data-testid={`${testIdBase}-incl-${it.key}`}
              title="Remove & rollback rate"
            >
              <span className="text-rose-500 text-[10px] flex-shrink-0 mt-0.5 group-hover:text-rose-700">−</span>
              <span className="flex-1 min-w-0">
                <span className="text-stone-900 font-medium">{it.label}</span>
                {it.spec && <div className="text-[9px] text-stone-500 truncate">{it.spec}</div>}
              </span>
              <span className="text-[10px] font-mono text-emerald-700 flex-shrink-0">+ ₹{(Number(it.rate) || 0).toLocaleString('en-IN')}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
